import type { AsyncSequence } from "../types/AsyncSequence"
import type { Time } from "../types/Units"
import { CopyIterable } from "../async/CopyIterable"
import { ReduceAsync } from "../async/ReduceAsync"
import { StringToUInt8 } from "../bytes/StringToUInt8"
import { Uint8Crc32 } from "../hashing/UInt8Crc32"
import { FromUnixTime } from "../time/FromUnixTime"
import { UnixTime } from "../time/UnixTime"

const writeArray = (size: number) => {
  const array = new Uint8Array(size)
  const view = new DataView(array.buffer)
  return {
    array,
    set16: (offset: number, value: number) =>
      view.setUint16(offset, value, true),
    set32: (offset: number, value: number) =>
      view.setUint32(offset, value, true),
  }
}

const dos_time = (timestamp: Time): number => {
  const date = FromUnixTime(timestamp)
  const seconds = Math.floor(date.getUTCSeconds() / 2)
  const minutes = date.getUTCMinutes()
  const hours = date.getUTCHours()
  return (hours << 11) | (minutes << 5) | seconds
}

const dos_date = (timestamp: Time): number => {
  const date = FromUnixTime(timestamp)
  const day = date.getUTCDate()
  const month = date.getUTCMonth() + 1
  const year = date.getUTCFullYear()
  return ((year - 1980) << 9) | (month << 5) | day
}

/**
 * Creates a ZIP archive from an async sequence of files.
 * Uses STORE compression method (no compression) with UTF-8 filenames.
 * Generates proper ZIP format with local headers, central directory, and EOCD.
 *
 * @param files - Async sequence of file objects with name, chunks, and optional modified time
 * @returns AsyncGenerator yielding Uint8Array chunks of the ZIP file
 *
 * @example
 * ```ts
 * import { ZipCreate } from "@pdfking/tfunc"
 *
 * const files = [
 *   {
 *     name: "hello.txt",
 *     chunks: [new TextEncoder().encode("Hello World")],
 *     modified: UnixTime()
 *   },
 *   {
 *     name: "data.json",
 *     chunks: [new TextEncoder().encode('{"key":"value"}')]
 *   }
 * ]
 *
 * const zipChunks = ZipCreate(files)
 * const blob = await BlobArray(zipChunks)
 * ```
 */
export const ZipCreate = async function* (
  files: AsyncSequence<{
    chunks: AsyncSequence<Uint8Array>
    modified?: Time
    name: string
  }>
): AsyncGenerator<Uint8Array> {
  const now = UnixTime()
  const centralDirectory: Uint8Array[] = []
  let offset = 0

  for await (const { chunks, modified, name } of files) {
    const unixTime = Math.floor(modified ?? now) as Time
    const time = dos_time(unixTime)
    const date = dos_date(unixTime)

    const [copy1, copy2, copy3] = CopyIterable(chunks, 3)

    const hash = Uint8Crc32(copy1)
    const sizePromise = ReduceAsync(
      (acc, chunk) => acc + chunk.length,
      copy2,
      0
    )

    const filenameBytes = StringToUInt8(name)
    const localHeaderOffset = offset

    // Wait for size and crc32
    const [size, crc32] = await Promise.all([sizePromise, hash])

    const {
      array: header,
      set16: headerSet16,
      set32: headerSet32,
    } = writeArray(30 + filenameBytes.length)

    headerSet32(0, 0x04034b50) // LOCAL_FILE_HEADER_SIGNATURE
    headerSet16(4, 10) // version needed to extract
    headerSet16(6, 0) // general purpose bit flag
    headerSet16(8, 0) // compression method (0 = store)
    headerSet16(10, time)
    headerSet16(12, date)
    headerSet32(14, crc32) // crc32
    headerSet32(18, size) // compressed size
    headerSet32(22, size) // uncompressed size
    headerSet16(26, filenameBytes.length) // filename length
    headerSet16(28, 0) // extra field length
    header.set(filenameBytes, 30)

    yield header
    offset += header.length

    for await (const chunk of copy3) {
      yield chunk
      offset += chunk.length
    }

    // Central Directory Entry
    const {
      array: central,
      set16: centralSet16,
      set32: centralSet32,
    } = writeArray(46 + filenameBytes.length)

    centralSet32(0, 0x02014b50) // CENTRAL_DIRECTORY_HEADER_SIGNATURE
    centralSet16(4, 20) // version made by (MS-DOS, v2.0)
    centralSet16(6, 10) // version needed to extract
    centralSet16(8, 0) // general purpose bit flag
    centralSet16(10, 0) // compression method
    centralSet16(12, time)
    centralSet16(14, date)
    centralSet32(16, crc32)
    centralSet32(20, size)
    centralSet32(24, size)
    centralSet16(28, filenameBytes.length)
    centralSet16(30, 0) // extra field length
    centralSet16(32, 0) // file comment length
    centralSet16(34, 0) // disk number start
    centralSet16(36, 0) // internal file attributes
    centralSet32(38, 0) // external file attributes
    centralSet32(42, localHeaderOffset) // relative offset of local header
    central.set(filenameBytes, 46)

    centralDirectory.push(central)
  }

  const centralDirStart = offset
  for (const entry of centralDirectory) {
    yield entry
    offset += entry.length
  }

  const { array: eocd, set16: eocdSet16, set32: eocdSet32 } = writeArray(22)
  eocdSet32(0, 0x06054b50) // END_OF_CENTRAL_DIR_SIGNATURE
  eocdSet16(4, 0) // number of this disk
  eocdSet16(6, 0) // disk where central directory starts
  eocdSet16(8, centralDirectory.length) // number of central dir records on this disk
  eocdSet16(10, centralDirectory.length) // total number of central dir records
  eocdSet32(12, offset - centralDirStart) // size of central directory
  eocdSet32(16, centralDirStart) // offset of start of central directory
  eocdSet16(20, 0) // comment length

  yield eocd
}
